import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/data/content";

export const Gallery = () => {
  const trackRef = useRef(null);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 640), behavior: "smooth" });
  };

  return (
    <section
      id="galeria"
      data-testid="gallery-section"
      className="bg-[#EAE4D9] py-20 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-xl">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#4A7055]">
              Galeria
            </span>
            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight text-[#2D3728] text-balance md:text-4xl lg:text-5xl">
              Zajrzyj do środka i nad wodę
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <p className="max-w-sm text-sm leading-relaxed text-[#6b7563]">
              Przeciągnij, aby zobaczyć więcej — prawdziwe zdjęcia obiektu o
              każdej porze roku.
            </p>
            <div className="hidden flex-shrink-0 items-center gap-2 sm:flex">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                aria-label="Poprzednie zdjęcia"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D8CFBC] bg-[#F9F7F3] text-[#2D3728] transition-colors hover:border-[#4A7055] hover:text-[#4A7055]"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                aria-label="Następne zdjęcia"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D8CFBC] bg-[#F9F7F3] text-[#2D3728] transition-colors hover:border-[#4A7055] hover:text-[#4A7055]"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        className="relative mt-12"
      >
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-[#EAE4D9] to-transparent md:w-24" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[#EAE4D9] to-transparent md:w-24" />

        <div
          ref={trackRef}
          className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 md:px-10"
        >
          {GALLERY.map((img, i) => (
            <div
              key={i}
              data-testid={`gallery-item-${i}`}
              className="group relative h-[300px] flex-none snap-start overflow-hidden rounded-[1.75rem] sm:h-[380px] lg:h-[480px]"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="h-full w-auto object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <span className="absolute bottom-4 left-5 translate-y-2 text-sm font-medium text-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                {img.alt}
              </span>
            </div>
          ))}
          <div className="w-1 flex-none md:w-6" aria-hidden="true" />
        </div>
      </motion.div>
    </section>
  );
};
